import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

/**
 * Consistent header for Settings destination pages launched from the
 * Settings Hub. Renders a back-to-Settings link, an accent icon tile,
 * a title and an optional subtitle. Keep page logic untouched — this is
 * purely the top-of-page chrome so every admin panel feels the same.
 */
export function SettingsPageHeader({ icon: Icon, title, subtitle, backTo = '/settings', actions, accent = 'indigo', titleTestId }) {
  const navigate = useNavigate();
  const accentCls = {
    indigo: 'bg-indigo-500/10 text-indigo-600',
    emerald: 'bg-emerald-500/10 text-emerald-600',
    sky: 'bg-sky-500/10 text-sky-600',
    amber: 'bg-amber-500/10 text-amber-600',
    rose: 'bg-rose-500/10 text-rose-600',
    purple: 'bg-purple-500/10 text-purple-600',
  }[accent] || 'bg-indigo-500/10 text-indigo-600';

  return (
    <div className="mb-6" data-testid="settings-page-header">
      <button
        onClick={() => navigate(backTo)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-3"
        data-testid="settings-header-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back to Settings
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${accentCls}`}>
              <Icon className="w-6 h-6" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-semibold tracking-tight font-outfit" data-testid={titleTestId}>{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export default SettingsPageHeader;

/** Standalone "Back to Settings" link for pages that keep their own rich header. */
export function SettingsBackLink({ backTo = '/settings' }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(backTo)}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-3"
      data-testid="settings-header-back"
    >
      <ChevronLeft className="w-4 h-4" /> Back to Settings
    </button>
  );
}
